import * as THREE from 'three';

const THUMBNAIL_WIDTH = 512;
const THUMBNAIL_HEIGHT = 384;
const DEFAULT_COLOR = '#0ea5e9';

interface QueueItem {
  readonly url: string;
  readonly resolve: (blob: Blob) => void;
  readonly reject: (error: Error) => void;
}

let renderer: THREE.WebGLRenderer | null = null;
const queue: QueueItem[] = [];
let isProcessing = false;

const getRenderer = (): THREE.WebGLRenderer => {
  // Recreate if the WebGL context was lost
  if (renderer) {
    const gl = renderer.getContext();
    if (gl.isContextLost()) {
      renderer.dispose();
      renderer = null;
    }
  }

  if (!renderer) {
    const canvas = document.createElement('canvas');
    canvas.width = THUMBNAIL_WIDTH;
    canvas.height = THUMBNAIL_HEIGHT;
    renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      preserveDrawingBuffer: true,
      alpha: false,
    });
    renderer.setSize(THUMBNAIL_WIDTH, THUMBNAIL_HEIGHT, false);
    renderer.setPixelRatio(1);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
  }
  return renderer;
};

const disposeObject = (obj: THREE.Object3D) => {
  obj.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.geometry.dispose();
      const material = child.material;
      if (Array.isArray(material)) {
        material.forEach((m) => m.dispose());
      } else {
        material.dispose();
      }
    }
  });
};

const renderThumbnail = async (url: string): Promise<Blob> => {
  const r = getRenderer();
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf1f5f9);

  // PBR environment (procedural studio lighting)
  const { RoomEnvironment } = await import('three/addons/environments/RoomEnvironment.js');
  const pmrem = new THREE.PMREMGenerator(r);
  scene.environment = pmrem.fromScene(new RoomEnvironment()).texture;
  pmrem.dispose();

  // Supplemental lights
  const fillLight = new THREE.DirectionalLight(0xffffff, 0.4);
  fillLight.position.set(-2, 3, 2);
  scene.add(fillLight);
  const rimLight = new THREE.DirectionalLight(0xffffff, 0.2);
  rimLight.position.set(2, -1, -3);
  scene.add(rimLight);

  // Camera
  const camera = new THREE.PerspectiveCamera(
    45,
    THUMBNAIL_WIDTH / THUMBNAIL_HEIGHT,
    0.1,
    10000,
  );

  // Fetch model data as ArrayBuffer, then parse synchronously.
  // This matches the working ModelViewer pattern and avoids issues
  // with ThreeMFLoader.load() + blob/presigned URLs via XHR.
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch model: ${response.status} ${response.statusText}`);
  }
  const buffer = await response.arrayBuffer();

  const { ThreeMFLoader } = await import('three/addons/loaders/3MFLoader.js');
  const loader = new ThreeMFLoader();
  const object = loader.parse(buffer);

  // Verify the model has renderable geometry
  let meshCount = 0;
  object.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      meshCount++;
      child.material = new THREE.MeshStandardMaterial({
        color: new THREE.Color(DEFAULT_COLOR),
        roughness: 0.65,
        metalness: 0.0,
      });
    }
  });

  if (meshCount === 0) {
    throw new Error('Model contains no renderable geometry');
  }

  // Z-up to Y-up rotation (same as ModelViewer)
  object.rotation.set(-Math.PI / 2, 0, 0);
  object.updateMatrixWorld(true);
  scene.add(object);

  // Center model
  const box = new THREE.Box3().setFromObject(object);
  const size = box.getSize(new THREE.Vector3());
  const rawCenter = box.getCenter(new THREE.Vector3());

  object.position.set(
    object.position.x - rawCenter.x,
    object.position.y - box.min.y,
    object.position.z - rawCenter.z,
  );

  // Recompute after centering
  const centeredBox = new THREE.Box3().setFromObject(object);
  const center = centeredBox.getCenter(new THREE.Vector3());

  // Position camera (isometric view)
  const maxDim = Math.max(size.x, size.y, size.z);
  const fov = camera.fov * (Math.PI / 180);
  const dist = (maxDim / (2 * Math.tan(fov / 2))) * 1.3;

  camera.position.set(
    center.x + dist * 0.7,
    center.y + dist * 0.5,
    center.z + dist * 0.7,
  );
  camera.lookAt(center);

  // Render
  r.render(scene, camera);

  // Capture as blob
  const canvas = r.domElement;
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))),
      'image/png',
    );
  });

  // Validate the blob has meaningful content (not just a blank canvas)
  if (blob.size < 1000) {
    throw new Error('Generated thumbnail appears to be empty');
  }

  // Cleanup scene objects (keep renderer for reuse)
  disposeObject(object);
  scene.remove(object);
  scene.environment?.dispose();
  fillLight.dispose();
  rimLight.dispose();
  scene.clear();

  return blob;
};

const processQueue = async () => {
  if (isProcessing || queue.length === 0) return;
  isProcessing = true;

  while (queue.length > 0) {
    const item = queue.shift()!;
    try {
      const blob = await renderThumbnail(item.url);
      item.resolve(blob);
    } catch (err) {
      item.reject(err instanceof Error ? err : new Error('Thumbnail render failed'));
    }
  }

  isProcessing = false;
};

export const generateThumbnail = (url: string): Promise<Blob> =>
  new Promise<Blob>((resolve, reject) => {
    queue.push({ url, resolve, reject });
    processQueue();
  });
